import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ScanSearch, ShieldAlert, ShieldCheck, Loader2, FolderPlus, Link2, Youtube } from "lucide-react";

const SEV = {
  schwer: { label: "Schwerer Verstoß", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  mittel: { label: "Mittlerer Verstoß", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  leicht: { label: "Leichter Verstoß", cls: "bg-yellow-50 text-yellow-700 border-yellow-200", dot: "bg-yellow-500" },
  keiner: { label: "Kein Verstoß", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
};

export default function CheckUrl() {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!url.trim() && !text.trim()) {
      toast.error("Bitte eine URL oder Text eingeben.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/check-url", { url: url.trim(), text: text.trim() });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prüfung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const saveCase = async () => {
    setSaving(true);
    try {
      const r = await api.post("/check-url", { url: url.trim(), text: text.trim(), save: true });
      setResult(r.data);
      toast.success("Als Fall in der Fallakte gespeichert.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const a = result?.assessment;
  const sev = a ? SEV[a.severity] || SEV.keiner : null;
  const isYoutube = result?.platform === "youtube";

  return (
    <div className="space-y-6" data-testid="check-url-page">
      <PageHeader
        title="URL / Video prüfen"
        subtitle="Prüfe einen beliebigen Link (YouTube, Presseartikel, Profil, Bewertungsseite) sofort auf Marken- und Compliance-Verstöße."
      />

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Link (URL)</label>
        <div className="mt-2 flex gap-2">
          <div className="relative flex-1">
            <Link2 className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A6A4A9]" />
            <Input
              data-testid="check-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="https://www.youtube.com/watch?v=…  oder  https://presseportal.de/artikel…"
              className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] pl-10 focus-visible:ring-[#0A0A0A]"
            />
          </div>
          <Button
            data-testid="check-url-btn"
            onClick={check}
            disabled={loading}
            className="rounded-md bg-[#0A0A0A] px-6 text-white hover:bg-[#262626]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}
            {loading ? "Prüft …" : "Prüfen"}
          </Button>
        </div>

        <label className="mt-5 block text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">
          Optional: Text / Transkript einfügen
        </label>
        <Textarea
          data-testid="check-url-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Bei YouTube wird das Transkript jetzt automatisch über Apify geladen und geprüft. Falls ein Video kein Transkript hat (oder für andere Inhalte), kannst du hier optional Text/Transkript einfügen."
          className="mt-2 min-h-[120px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] text-sm focus-visible:ring-[#0A0A0A]"
        />
      </Card>

      {result && (
        <Card
          data-testid="check-url-result"
          className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {a.is_violation ? (
                <ShieldAlert className="h-6 w-6 text-rose-600" />
              ) : (
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
              )}
              <div>
                <p className="font-heading text-lg font-bold text-[#0A0A0A]">
                  {a.is_violation ? "Verstoß erkannt" : "Kein Verstoß erkannt"}
                </p>
                <p className="text-xs text-[#8B8A8E]">KI-Konfidenz: {a.confidence}%</p>
              </div>
            </div>
            <span
              data-testid="check-url-severity"
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold ${sev.cls}`}
            >
              <span className={`h-2 w-2 rounded-full ${sev.dot}`} />
              {sev.label}
            </span>
          </div>

          {result.title && (
            <div className="mt-5 flex items-start gap-2 rounded-md bg-[#FAF7F2] p-3">
              {isYoutube ? <Youtube className="mt-0.5 h-4 w-4 text-[#A6A4A9]" /> : <Link2 className="mt-0.5 h-4 w-4 text-[#A6A4A9]" />}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#0A0A0A]">{result.title}</p>
                {result.author && <p className="text-xs text-[#8B8A8E]">Kanal/Autor: {result.author}</p>}
                {result.url && (
                  <a href={result.url} target="_blank" rel="noreferrer" className="truncate text-xs text-blue-600 hover:underline">
                    {result.url}
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">KI-Begründung</p>
            <p className="mt-1.5 text-sm leading-relaxed text-[#3A3A3C]">{a.reason}</p>
          </div>

          {result.note && (
            <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {result.note}
            </p>
          )}

          <div className="mt-6 flex items-center gap-3">
            <Button
              data-testid="check-url-save-btn"
              onClick={saveCase}
              disabled={saving || !!result.saved_case_id}
              className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderPlus className="mr-2 h-4 w-4" />}
              {result.saved_case_id ? "In Fallakte gespeichert ✓" : "Als Fall speichern"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
